import { Routes } from '@packages/enums';
import { withSuspense } from '@ui/components/layout/LazyPage';
import EditSource from '@ui/features/SourceRegistry/components/Actions/EditSource';
import EditCohortForm from '@ui/pages/Cohorts/EditForm';
import { lazy } from 'react';
import { Outlet, type RouteObject } from 'react-router';

const Home = lazy(() => import('../../pages/Dashboard'));
const DataCuration = lazy(() => import('../../pages/DataCuration'));
const NewCurationForm = lazy(() => import('../../pages/DataCuration/Form'));
const CurationResults = lazy(() => import('../../features/CurationResults'));
const ProgressTracking = lazy(() => import('../../features/DataCuration/pages/ProgressTracking'));
const DeIdentification = lazy(() => import('../../pages/DeIdentification'));
const NewDeID = lazy(() => import('../../pages/DeIdentification/NewDeID'));
const DeIDDetails = lazy(() => import('../../pages/DeIdentification/DeIDDetails'));
const Calculations = lazy(() => import('../../pages/Calculations'));
const DocumentViewer = lazy(() => import('../../features/DataCuration/components/DocumentViewer'));
const NewCalculationForm = lazy(() => import('../../pages/Calculations/NewCalculation'));
const Chat = lazy(() => import('../../pages/Chat'));
const Cohorts = lazy(() => import('../../pages/Cohorts'));
const CohortDetails = lazy(() => import('../../pages/Cohorts/CohortDetails'));
const NewCohortForm = lazy(() => import('../../pages/Cohorts/Form'));
const Patients = lazy(() => import('../../pages/Patients'));
const SourceRegistry = lazy(() => import('../../pages/SourceRegistry'));
const StudyBrowserPage = lazy(() => import('../../features/SourceRegistry/components/StudyBrowser/StudyBrowserPage'));
const DataIngestion = lazy(() => import('../../pages/DataIngestion'));
const AdvancedIngestion = lazy(() => import('../../pages/DataIngestion/AdvancedIngestion'));
const TemplateManagement = lazy(() => import('../../pages/DataIngestion/TemplateManagement'));
const License = lazy(() => import('../../pages/License'));
const PatientPage = lazy(() => import('../../pages/PatientDetails'));
const Ontologies = lazy(() => import('../../pages/Ontologies'));
const OntologyView = lazy(() => import('../../pages/Ontologies/View'));
const OntologyForm = lazy(() => import('../../pages/Ontologies/Form'));
const OntologyBuilder = lazy(() => import('../../features/Ontologies/components/OntologyBuilder'));
const DeIDProfiles = lazy(() => import('../../pages/DeIdentification/Profiles'));
const DeIDProfileForm = lazy(() => import('../../pages/DeIdentification/Profiles/Form'));
const DbExplorer = lazy(() => import('../../pages/DbExplorer'));
const DbChatPage = lazy(() => import('../../pages/DbChat'));
const Datasets = lazy(() => import('../../pages/Datasets'));
const DatasetForm = lazy(() => import('../../pages/Datasets/Form'));
const DatasetDetails = lazy(() => import('../../pages/Datasets/Details'));
const Audit = lazy(() => import('../../pages/Audit'));
const KBManagement = lazy(() => import('../../features/QueryBotKB/pages/KBManagement'));
const Administration = lazy(() => import('../../pages/Administration'));
const Feedback = lazy(() => import('../../pages/Feedback'));
const ClinicalTrials = lazy(() => import('../../pages/ClinicalTrials'));
const ClinicalTrialJobDetails = lazy(() => import('../../pages/ClinicalTrials/JobDetails'));
const CpgRag = lazy(() => import('../../pages/CpgRag'));
const McpDocsPage = lazy(() => import('../../pages/McpDocs').then(module => ({ default: module.McpDocsPage })));
const Settings = lazy(() => import('../../pages/Settings'));
const RegistryStandards = lazy(() => import('../../pages/RegistryStandards'));
const StandardDetailPage = lazy(() => import('../../pages/RegistryStandards/StandardDetailPage'));
const VersionDetailPage = lazy(() => import('../../pages/RegistryStandards/VersionDetailPage'));
const FieldDetailPage = lazy(() => import('../../pages/RegistryStandards/FieldDetailPage'));
const RulesetDetailPage = lazy(() => import('../../pages/RegistryStandards/RulesetDetailPage'));
const TerminologyServer = lazy(() => import('../../pages/TerminologyServer'));

export const patientJourneyRoutes: RouteObject[] = [
    {
        path: Routes.HOME,
        handle: { crumb: 'Home' },
        element: withSuspense(Home),
    },
    {
        path: `${Routes.CHAT}/:conversationId?`,
        handle: { crumb: 'CoPilot' },
        element: withSuspense(Chat),
    },
    {
        path: Routes.COHORTS,
        handle: { crumb: 'Cohort Builder' },
        children: [
            {
                index: true,
                element: withSuspense(Cohorts),
            },
            {
                path: 'new',
                handle: { crumb: 'New Cohort' },
                element: withSuspense(NewCohortForm),
            },
            {
                path: ':cohort_id/edit',
                handle: { crumb: 'Edit Cohort' },
                element: withSuspense(EditCohortForm),
            },
            {
                path: 'clinical-trials',
                handle: { crumb: 'Clinical Trials' },
                children: [
                    {
                        index: true,
                        element: withSuspense(ClinicalTrials),
                    },
                    {
                        path: ':id',
                        handle: { crumb: 'Job Details' },
                        element: withSuspense(ClinicalTrialJobDetails),
                    },
                ],
            },
            {
                path: ':cohort_id',
                handle: { crumb: 'Cohort Details' },
                element: withSuspense(CohortDetails),
            },
        ],
    },
    {
        path: Routes.PATIENTS,
        handle: { crumb: 'Patient Journey' },
        element: <Outlet />,
        children: [
            {
                index: true,
                element: withSuspense(Patients),
            },
            {
                path: ':id',
                handle: {
                    crumb: 'Patient Details',
                },
                element: withSuspense(PatientPage),
            },
        ],
    },

    {
        handle: { crumb: 'Data Integration' },
        children: [
            {
                path: Routes.SOURCE_REGISTRY,
                handle: { crumb: 'Source Registry' },
                children: [
                    {
                        index: true,
                        element: withSuspense(SourceRegistry),
                    },
                    {
                        path: ':id',
                        handle: { crumb: 'Edit Source' },
                        element: withSuspense(EditSource),
                    },
                    {
                        path: ':sourceId/study-browser',
                        handle: { crumb: 'Study Browser' },
                        element: withSuspense(StudyBrowserPage),
                    },
                ],
            },
            {
                path: Routes.DB_EXPLORER,
                handle: { crumb: 'Database Explorer' },
                element: withSuspense(DbExplorer),
            },
            {
                path: '/data-integration/db-chat',
                handle: { crumb: 'CoPilot' },
                element: withSuspense(DbChatPage),
            },
            {
                path: '/data-integration/datasets',
                handle: { crumb: 'Datasets' },
                children: [
                    {
                        index: true,
                        element: withSuspense(Datasets),
                    },
                    {
                        path: 'new',
                        handle: { crumb: 'New Dataset' },
                        element: withSuspense(DatasetForm),
                    },
                    {
                        path: ':id',
                        handle: { crumb: 'Dataset Details' },
                        element: withSuspense(DatasetDetails),
                    },
                ],
            },
            {
                path: Routes.DATA_INGESTION,
                handle: { crumb: 'Data Ingestion' },
                children: [
                    {
                        index: true,
                        element: withSuspense(DataIngestion),
                    },
                    {
                        path: 'new',
                        handle: { crumb: 'Add New Ingestion' },
                        element: withSuspense(AdvancedIngestion),
                    },
                    {
                        path: 'templates',
                        handle: { crumb: 'Job Templates' },
                        element: withSuspense(TemplateManagement),
                    },
                ],
            },
            {
                path: Routes.REGISTRY_STANDARDS,
                handle: { crumb: 'Registry Standards' },
                element: withSuspense(RegistryStandards),
                children: [
                    {
                        path: 'versions/:versionId',
                        handle: { crumb: 'Version Detail' },
                        element: withSuspense(VersionDetailPage),
                    },
                    {
                        path: 'fields/:fieldId',
                        handle: { crumb: 'Field Detail' },
                        element: withSuspense(FieldDetailPage),
                    },
                    {
                        path: 'clinical-rulesets/:id',
                        handle: { crumb: 'Ruleset Detail' },
                        element: withSuspense(RulesetDetailPage),
                    },
                    {
                        path: ':id',
                        handle: { crumb: 'Standard Detail' },
                        element: withSuspense(StandardDetailPage),
                    },
                ],
            },
        ],
    },
    {
        path: Routes.LICENSE,
        handle: { crumb: 'License' },
        element: withSuspense(License),
    },
    {
        // Direct entry points for document viewers (same paths also nested under Data Curation Studio children for breadcrumbs).
        path: Routes.DOCUMENT_VIEWER,
        element: <DocumentViewer />,
    },
    {
        path: '/administration',
        handle: { crumb: 'Administration' },
        element: withSuspense(Administration),
    },
    {
        path: Routes.KB_MANAGEMENT,
        handle: { crumb: 'KB Management' },
        element: withSuspense(KBManagement),
    },

    {
        path: Routes.DATA_CURATION,
        handle: { crumb: 'Data Curation Studio' },
        children: [
            {
                index: true,
                element: withSuspense(DataCuration),
            },
            {
                path: 'new',
                handle: { crumb: 'New Automation' },
                element: withSuspense(NewCurationForm),
            },
            {
                path: ':id',
                handle: { crumb: 'Curation Details' },
                element: withSuspense(CurationResults),
            },
            {
                path: ':id/progress',
                handle: { crumb: 'Progress Tracking' },
                element: withSuspense(ProgressTracking),
            },
            {
                path: 'document-viewer',
                handle: { crumb: 'Document Viewer' },
                element: withSuspense(DocumentViewer),
            },
            {
                path: Routes.ONTOLOGIES,
                handle: { crumb: 'Registries' },
                children: [
                    {
                        index: true,
                        element: withSuspense(Ontologies),
                    },
                    {
                        path: 'build',
                        handle: { crumb: 'Build with AI' },
                        element: withSuspense(OntologyBuilder),
                    },
                    {
                        path: 'new',
                        handle: { crumb: 'New Registry' },
                        element: withSuspense(OntologyForm),
                    },
                    {
                        path: 'view/:id',
                        handle: { crumb: 'View Registry' },
                        element: withSuspense(OntologyView),
                    },
                    {
                        path: 'edit/:id',
                        handle: { crumb: 'Edit Registry' },
                        element: withSuspense(OntologyForm),
                    },
                ],
            },
        ],
    },
    {
        path: Routes.DE_IDENTIFICATION,
        handle: { crumb: 'De-Identification' },
        children: [
            {
                index: true,
                element: withSuspense(DeIdentification),
            },
            {
                path: 'new',
                handle: { crumb: 'New De-Identification' },
                element: withSuspense(NewDeID),
            },
            {
                path: 'profiles',
                handle: { crumb: 'De-Identification Profiles' },
                children: [
                    {
                        index: true,
                        element: withSuspense(DeIDProfiles),
                    },
                    {
                        path: 'new',
                        handle: { crumb: 'New Profile' },
                        element: withSuspense(DeIDProfileForm),
                    },
                    {
                        path: 'view/:id',
                        handle: { crumb: 'View Profile' },
                        element: withSuspense(DeIDProfileForm),
                    },
                    {
                        path: 'edit/:id',
                        handle: { crumb: 'Edit Profile' },
                        element: withSuspense(DeIDProfileForm),
                    },
                ],
            },
            {
                path: ':id',
                handle: { crumb: 'De-Identification Details' },
                element: withSuspense(DeIDDetails),
            },
        ],
    },
    {
        path: Routes.CLINICAL_MEASURES,
        handle: { crumb: 'Clinical Measures' },
        children: [
            {
                index: true,
                element: withSuspense(Calculations),
            },
            {
                path: 'new',
                handle: { crumb: 'New Clinical Measure' },
                element: withSuspense(NewCalculationForm),
            },
        ],
    },

    {
        path: Routes.AUDIT,
        handle: { crumb: 'Audit Logs' },
        element: withSuspense(Audit),
    },
    {
        path: Routes.CPG_RAG,
        handle: { crumb: 'CPG RAG' },
        element: withSuspense(CpgRag),
    },
    {
        path: Routes.MCP_DOCS,
        handle: { crumb: 'MCP Docs' },
        element: withSuspense(McpDocsPage),
    },
    {
        path: Routes.TERMINOLOGY_SERVER,
        handle: { crumb: 'Terminology Server' },
        element: withSuspense(TerminologyServer),
    },
    {
        path: Routes.FEEDBACK,
        handle: { crumb: 'Feedback' },
        element: withSuspense(Feedback),
    },
    {
        path: Routes.SETTINGS,
        handle: { crumb: 'Settings' },
        element: withSuspense(Settings),
    },
];
